import { useNavigate, useParams } from '@tanstack/react-router';
import { MoreHorizontal, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { useStore } from '@/shared/store/store-context';

import { AccountEmailDialog } from '../components/account-email-dialog';
import { ChangeEmailDialog } from '../components/change-email-dialog';
import { CustomerAddressesCard } from '../components/customer-addresses-card';
import { CustomerGiftCardsCard } from '../components/customer-gift-cards-card';
import { CustomerNotesCard } from '../components/customer-notes-card';
import { CustomerOrdersCard } from '../components/customer-orders-card';
import { CustomerOverviewCard } from '../components/customer-overview-card';
import { CustomerPrivacyCard } from '../components/customer-privacy-card';
import { CustomerStatusBadge } from '../components/customer-status-badge';
import { CustomerTimelineCard } from '../components/customer-timeline-card';
import { DeleteCustomerDialog } from '../components/delete-customer-dialog';
import { DoNotSellBadge } from '../components/do-not-sell-badge';
import { EditCustomerDialog } from '../components/edit-customer-dialog';
import { MergeCustomerDialog } from '../components/merge-customer-dialog';
import { useCustomer } from '../hooks/use-customer';
import { customerListSearchSchema } from '../schemas/customer-list-search';
import { customerName } from '../schemas/customer-rules';

const ROUTE = '/_authed/_active/s/$storeId/customers/$customerId' as const;

type Open = 'edit' | 'email' | 'account' | 'merge' | 'delete' | null;

function CustomerDetail() {
  const { t } = useTranslation('customers');
  const store = useStore();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { customerId } = useParams({ from: ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: customer } = useCustomer(store.id, customerId);
  const [open, setOpen] = useState<Open>(null);
  if (!customer) return null;

  const dialog = (name: Exclude<Open, null>) => ({
    open: open === name,
    onOpenChange: (next: boolean) => setOpen(next ? name : null),
  });
  const accountKind = customer.hasPassword ? 'password_reset' : 'invite';

  return (
    <PageContent>
      <PageHeader
        title={customerName(customer) ?? t('guestName')}
        meta={
          <>
            <CustomerStatusBadge status={customer.status} />
            {customer.doNotSell ? <DoNotSellBadge /> : null}
          </>
        }
        description={customer.email}
        backTo={{ to: '/s/$storeId/customers', params: { storeId: store.id } }}
        backLabel={t('detail.back')}
        actions={
          <Can permission="customers.write">
            <Button variant="outline" onClick={() => setOpen('edit')}>
              <Pencil aria-hidden />
              {t('edit.action')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('actions.more')}
                  data-tour="customers.detail.more-menu"
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setOpen('email')}>
                  {t('changeEmail.action')}
                </DropdownMenuItem>
                {customer.status === 'disabled' ? null : (
                  <DropdownMenuItem onSelect={() => setOpen('account')}>
                    {t(`accountEmail.${accountKind}.action`)}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setOpen('merge')}>
                  {t('merge.action')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setOpen('delete')}>
                  {t('delete.action')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Can>
        }
      />
      <FormLayout
        aside={
          <>
            <CustomerNotesCard
              storeId={store.id}
              customer={customer}
              readOnly={!can('customers.write')}
            />
            <CustomerPrivacyCard storeId={store.id} customer={customer} />
          </>
        }
      >
        <CustomerOverviewCard customer={customer} />
        <CustomerAddressesCard storeId={store.id} customerId={customer.id} />
        {can('orders.read') ? (
          <CustomerOrdersCard storeId={store.id} customerId={customer.id} />
        ) : null}
        {can('gift_cards.read') ? (
          <CustomerGiftCardsCard storeId={store.id} customerId={customer.id} />
        ) : null}
        <CustomerTimelineCard storeId={store.id} customerId={customer.id} />
      </FormLayout>
      {open === 'edit' ? (
        <EditCustomerDialog storeId={store.id} customer={customer} {...dialog('edit')} />
      ) : null}
      {open === 'email' ? (
        <ChangeEmailDialog storeId={store.id} customer={customer} {...dialog('email')} />
      ) : null}
      {open === 'account' ? (
        <AccountEmailDialog
          storeId={store.id}
          customer={customer}
          kind={accountKind}
          {...dialog('account')}
        />
      ) : null}
      {open === 'merge' ? (
        <MergeCustomerDialog
          storeId={store.id}
          customer={customer}
          {...dialog('merge')}
          onMerged={(survivor) => {
            if (survivor.id === customer.id) return;
            void navigate({
              to: '/s/$storeId/customers/$customerId',
              params: { storeId: store.id, customerId: survivor.id },
            });
          }}
        />
      ) : null}
      {open === 'delete' ? (
        <DeleteCustomerDialog
          storeId={store.id}
          customer={customer}
          {...dialog('delete')}
          onDeleted={() =>
            void navigate({
              to: '/s/$storeId/customers',
              params: { storeId: store.id },
              search: customerListSearchSchema.parse({}),
            })
          }
        />
      ) : null}
    </PageContent>
  );
}

export function CustomerDetailPage() {
  return (
    <FeatureErrorBoundary name="customers.detail">
      <CustomerDetail />
    </FeatureErrorBoundary>
  );
}
